import { useAuth } from "@/components/AuthContext";
import {
  Conversation as BackendConversation,
  ConversationMessage as BackendMessage,
  createConversation as apiCreate,
  deleteConversationAPI as apiDelete,
  listConversations as apiList,
  migrateConversations as apiMigrate,
  updateConversationAPI as apiUpdate,
  readLocalConversations,
  writeLocalConversations,
} from "@/lib/conversationAPI";
import { GUEST_SESSION_CLAIM_EVENT, readPendingGuestSessionClaim } from "@/lib/guestSessionClaim";
import { createLogger } from "@/lib/logger";
import { useCallback, useEffect, useMemo, useState } from "react";

const logger = createLogger("conversation-history");
export const MIN_PAST_CONVERSATION_MESSAGES = 2;

export interface ConversationMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

export interface Conversation {
  id: string;
  title: string;
  messages: ConversationMessage[];
  languageName: string;
  languageLevel: "absolute_beginner" | "beginner" | "intermediate" | "advanced";
  createdAt: number;
  updatedAt: number;
  conversationKind: "writing" | "speaking";
  continuedFromConversationId: string | null;
  assistantTurnCount: number;
  canContinue: boolean;
}

export const useConversationHistory = (onConversationActivity?: () => void) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingBackend, setUsingBackend] = useState(false);

  // Helpers
  const sortByUpdatedDesc = useCallback(
    (items: Conversation[]) => items.slice().sort((a, b) => b.updatedAt - a.updatedAt),
    []
  );

  const saveToStorage = useCallback((convs: Conversation[]) => {
    try {
      writeLocalConversations(convs);
    } catch (err) {
      logger.error("Failed to save conversations", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, []);

  // Generate conversation title from first user message
  const generateTitle = useCallback(
    (messages: ConversationMessage[], currentTitle?: string): string => {
      const firstUserMessage = messages.find((m) => m.role === "user");
      if (
        firstUserMessage &&
        currentTitle &&
        (currentTitle.includes("Session") || currentTitle === "New Conversation")
      ) {
        const content = firstUserMessage.content.trim();
        if (
          content.toLowerCase().includes("hello let's start practicing") ||
          content.toLowerCase().includes("please explain this phrase")
        ) {
          return currentTitle;
        }
        const newTitle = content.length <= 50 ? content : content.substring(0, 47) + "...";
        return newTitle;
      }
      return currentTitle || "New Conversation";
    },
    []
  );

  // Initial load: try backend if authenticated; else localStorage
  useEffect(() => {
    let cancelled = false;
    async function init() {
      setLoading(true);
      setError(null);
      try {
        if (userId) {
          // Try backend
          const backend = await apiList();
          if (cancelled) return;

          // Auto-migrate only if user has local conversations and backend is empty
          const pendingClaim = readPendingGuestSessionClaim();
          const localConvs = readLocalConversations().filter(
            (conversation) => conversation.id !== pendingClaim?.localConversationId
          );
          if (localConvs.length > 0 && backend.length === 0) {
            try {
              await apiMigrate(localConvs, "merge");
              const after = await apiList();
              if (cancelled) return;
              setConversations(sortByUpdatedDesc(after));
              setUsingBackend(true);
              setLoading(false);
              return;
            } catch (merr) {
              logger.warn("Automatic migration failed, falling back to backend list", {
                error: merr instanceof Error ? merr.message : String(merr),
              });
            }
          }

          setConversations(sortByUpdatedDesc(backend));
          setUsingBackend(true);
          setLoading(false);
          return;
        }
      } catch (e: any) {
        logger.warn("Backend conversations unavailable, using localStorage", {
          error: e?.message || String(e),
        });
      }

      // Fallback to localStorage
      try {
        const local = readLocalConversations();
        if (!cancelled) {
          setConversations(sortByUpdatedDesc(local));
          setUsingBackend(false);
        }
      } catch (err) {
        if (!cancelled) setError("Failed to load conversations from local storage");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    init();
    return () => {
      cancelled = true;
    };
  }, [userId, sortByUpdatedDesc]);

  useEffect(() => {
    const handleClaimedConversation = (event: Event) => {
      const detail = (event as CustomEvent<{ localConversationId: string; conversationId: string }>)
        .detail;
      if (!detail) return;
      if (currentConversationId === detail.localConversationId) {
        // Claiming preserves the guest session in history; it does not resume it.
        // Clear the old local selection so the authenticated experience creates
        // a genuinely fresh conversation.
        setCurrentConversationId(null);
      }
      if (userId) {
        void apiList()
          .then((items) => {
            setConversations(sortByUpdatedDesc(items));
            setUsingBackend(true);
          })
          .catch((claimRefreshError) => {
            logger.warn("Failed to refresh history after guest session claim", {
              error:
                claimRefreshError instanceof Error
                  ? claimRefreshError.message
                  : String(claimRefreshError),
            });
          });
      }
    };
    window.addEventListener(GUEST_SESSION_CLAIM_EVENT, handleClaimedConversation);
    return () => window.removeEventListener(GUEST_SESSION_CLAIM_EVENT, handleClaimedConversation);
  }, [currentConversationId, sortByUpdatedDesc, userId]);

  // Create a new conversation
  const createNewConversation = useCallback(
    async (
      languageName: string,
      languageLevel: "absolute_beginner" | "beginner" | "intermediate" | "advanced",
      conversationKind: "writing" | "speaking" = "writing"
    ): Promise<string> => {
      const conversationNumber =
        conversations.filter((c) => c.languageName === languageName).length + 1;
      const newTitle = `${languageName} Session ${conversationNumber}`;

      if (usingBackend && userId) {
        // Backend create
        try {
          const conv = await apiCreate({
            title: newTitle,
            languageName,
            languageLevel,
            conversationKind,
          });
          setConversations((prev) => sortByUpdatedDesc([conv, ...prev]));
          setCurrentConversationId(conv.id);
          // Notify about conversation activity (for streak update)
          onConversationActivity?.();
          return conv.id;
        } catch (err) {
          logger.error("Failed to create conversation via API, falling back to local", {
            error: err instanceof Error ? err.message : String(err),
          });
          // Fall back to local create
          const local: Conversation = {
            id: `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            title: newTitle,
            messages: [],
            languageName,
            languageLevel,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            conversationKind,
            continuedFromConversationId: null,
            assistantTurnCount: 0,
            canContinue: false,
          };
          setConversations((prev) => {
            const updated = sortByUpdatedDesc([local, ...prev]);
            saveToStorage(updated);
            return updated;
          });
          setUsingBackend(false);
          setCurrentConversationId(local.id);
          return local.id;
        }
      }

      // Local create
      const local: Conversation = {
        id: `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title: newTitle,
        messages: [],
        languageName,
        languageLevel,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        conversationKind,
        continuedFromConversationId: null,
        assistantTurnCount: 0,
        canContinue: false,
      };
      setConversations((prev) => {
        const updated = sortByUpdatedDesc([local, ...prev]);
        saveToStorage(updated);
        return updated;
      });
      setCurrentConversationId(local.id);
      return local.id;
    },
    [conversations, usingBackend, userId, sortByUpdatedDesc, saveToStorage]
  );

  const adoptConversation = useCallback(
    (conversation: BackendConversation) => {
      setConversations((previous) =>
        sortByUpdatedDesc([
          conversation,
          ...previous.filter((candidate) => candidate.id !== conversation.id),
        ])
      );
      setCurrentConversationId(conversation.id);
    },
    [sortByUpdatedDesc]
  );

  // Update conversation with new messages
  const updateConversation = useCallback(
    (conversationId: string, messages: ConversationMessage[]) => {
      const current = conversations.find((c) => c.id === conversationId);
      if (!current) return;

      const newTitle =
        current.languageName === "Maths" ? current.title : generateTitle(messages, current.title);

      if (usingBackend && userId) {
        apiUpdate(conversationId, {
          title: newTitle,
          messages: messages as BackendMessage[],
        })
          .then((updated) => {
            setConversations((prev) =>
              sortByUpdatedDesc(prev.map((c) => (c.id === conversationId ? updated : c)))
            );
            // Notify about conversation activity (for streak update)
            onConversationActivity?.();
          })
          .catch((err) => {
            logger.error("Failed to update via API, keeping local state only", {
              error: err instanceof Error ? err.message : String(err),
            });
            // Soft fallback: update local state too
            setConversations((prev) => {
              const updated = prev
                .map((conv) =>
                  conv.id === conversationId
                    ? { ...conv, messages, title: newTitle, updatedAt: Date.now() }
                    : conv
                )
                .sort((a, b) => b.updatedAt - a.updatedAt);
              saveToStorage(updated);
              setUsingBackend(false);
              return updated;
            });
          });
        return;
      }

      // Local update
      setConversations((prev) => {
        const updated = prev
          .map((conv) =>
            conv.id === conversationId
              ? { ...conv, messages, title: newTitle, updatedAt: Date.now() }
              : conv
          )
          .sort((a, b) => b.updatedAt - a.updatedAt);
        saveToStorage(updated);
        return updated;
      });
    },
    [conversations, generateTitle, usingBackend, userId, saveToStorage, sortByUpdatedDesc]
  );

  // Delete a conversation
  const deleteConversation = useCallback(
    (conversationId: string) => {
      if (usingBackend && userId) {
        apiDelete(conversationId)
          .then(() => {
            setConversations((prev) => prev.filter((c) => c.id !== conversationId));
          })
          .catch((err) => {
            logger.error("Failed to delete via API, falling back to local", {
              error: err instanceof Error ? err.message : String(err),
            });
            setUsingBackend(false);
            setConversations((prev) => {
              const updated = prev.filter((conv) => conv.id !== conversationId);
              saveToStorage(updated);
              return updated;
            });
          });
      } else {
        setConversations((prev) => {
          const updated = prev.filter((conv) => conv.id !== conversationId);
          saveToStorage(updated);
          return updated;
        });
      }

      if (currentConversationId === conversationId) {
        setCurrentConversationId(null);
      }
    },
    [currentConversationId, usingBackend, userId, saveToStorage]
  );

  // Get a specific conversation
  const getConversation = useCallback(
    (conversationId: string) => conversations.find((conv) => conv.id === conversationId),
    [conversations]
  );

  const pastConversations = useMemo(
    () =>
      conversations.filter(
        (conversation) =>
          conversation.messages.filter((message) => message.content.trim().length > 0).length >=
          MIN_PAST_CONVERSATION_MESSAGES
      ),
    [conversations]
  );

  // Filter eligible past conversations based on search query
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return pastConversations;
    const query = searchQuery.toLowerCase();
    return pastConversations.filter(
      (conv) =>
        conv.title.toLowerCase().includes(query) ||
        conv.languageName.toLowerCase().includes(query) ||
        conv.messages.some((msg) => msg.content.toLowerCase().includes(query))
    );
  }, [pastConversations, searchQuery]);

  // Get conversation by current ID
  const currentConversation = currentConversationId ? getConversation(currentConversationId) : null;

  // Refresh from source
  const refreshConversations = useCallback(async () => {
    const shouldBlockWithLoadingState = conversations.length === 0;
    if (shouldBlockWithLoadingState) setLoading(true);
    setError(null);
    try {
      if (usingBackend && userId) {
        const list = await apiList();
        setConversations(sortByUpdatedDesc(list));
        return;
      }
      const local = readLocalConversations();
      setConversations(sortByUpdatedDesc(local));
    } catch (error) {
      logger.error("Failed to refresh conversations", {
        error: error instanceof Error ? error.message : String(error),
      });
      if (shouldBlockWithLoadingState) setError("Failed to load conversations");
    } finally {
      if (shouldBlockWithLoadingState) setLoading(false);
    }
  }, [conversations.length, usingBackend, userId, sortByUpdatedDesc]);

  // Manual migration (used by settings UI)
  const migrateToBackend = useCallback(
    async (strategy: "merge" | "replace" = "merge") => {
      if (!userId) throw new Error("You must be signed in to migrate.");
      const pendingClaim = readPendingGuestSessionClaim();
      const local = readLocalConversations().filter(
        (conversation) => conversation.id !== pendingClaim?.localConversationId
      );
      await apiMigrate(local, strategy);
      const after = await apiList();
      setConversations(sortByUpdatedDesc(after));
      setUsingBackend(true);
    },
    [userId, sortByUpdatedDesc]
  );

  return {
    conversations: filteredConversations,
    allConversations: conversations,
    currentConversation,
    currentConversationId,
    searchQuery,
    setSearchQuery,
    setCurrentConversationId,
    createNewConversation,
    adoptConversation,
    updateConversation,
    deleteConversation,
    getConversation,
    refreshConversations,
    // New extras
    loading,
    error,
    usingBackend,
    migrateToBackend,
  };
};
